/**
 * Integrity signals for private runs (god-only).
 *
 * Business model: the banker is a stakeholder in the house (oversight, results, caps).
 * Operational fraud is most likely in the lister–runner chain (plays vs. collections).
 */
import {
  detectCollectionWithoutPlays,
  detectConfigTamperBurst,
  detectLedgerSkew,
  detectListerRunnerCollusion,
  detectPlaysAfterCollection,
  detectPostResultPlays,
  detectSuspendedHouseActivity,
} from './integrityDetectors';

export type IntegritySignalKind = 'conspiracy' | 'corruption' | 'sabotage';

export type IntegritySignalSeverity = 'warn' | 'danger';

export type IntegrityDisposition = 'open' | 'tp' | 'fp' | 'suppressed';

export interface IntegritySignal {
  kind: IntegritySignalKind;
  severity: IntegritySignalSeverity;
  code: string;
  houseId?: string;
  messageKey: string;
  messageParams?: Record<string, string | number>;
  evidence: Record<string, unknown>;
  disposition?: IntegrityDisposition;
  ticketId?: string | null;
}

export interface IntegrityAuditEvent {
  id?: string;
  action?: string;
  msg?: string;
  ts?: number;
  t?: string;
  actor?: { role?: string; houseId?: string; name?: string; uid?: string };
}

export interface IntegrityHouseRow {
  id: string;
  name?: string;
  coinsIn?: number;
  coinsOut?: number;
  status?: string;
}

export interface IntegrityScanInput {
  auditEvents: IntegrityAuditEvent[];
  houses: IntegrityHouseRow[];
  runId?: string;
}

export {
  detectCollectionWithoutPlays,
  detectConfigTamperBurst,
  detectLedgerSkew,
  detectListerRunnerCollusion,
  detectPlaysAfterCollection,
  detectPostResultPlays,
  detectSuspendedHouseActivity,
};

export function evaluateIntegritySignals(input: IntegrityScanInput): IntegritySignal[] {
  const { auditEvents, houses } = input;
  const merged = [
    ...detectPlaysAfterCollection(auditEvents),
    ...detectCollectionWithoutPlays(auditEvents),
    ...detectLedgerSkew(houses),
    ...detectSuspendedHouseActivity(auditEvents, houses),
    ...detectConfigTamperBurst(auditEvents),
    ...detectPostResultPlays(auditEvents),
  ];

  const seen = new Set<string>();
  return merged.filter((signal) => {
    const key = `${signal.code}:${signal.houseId ?? 'global'}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
